import { apiClient } from '@/lib/api/client';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  consentApi,
  MarketingNoticeStaleError,
} from '@/lib/api/consents';
import type {
  MarketingConsentNotice,
  MarketingConsentProjection,
  MarketingConsentStatus,
} from '@/lib/api/types';

export interface UseMarketingConsentOptions {
  enabled: boolean;
  customerKey: number | string | null;
}

export interface UseMarketingConsentReturn {
  notice: MarketingConsentNotice | null;
  consent: MarketingConsentProjection | null;
  isInitialLoading: boolean;
  initialError: string | null;
  retry: () => void;
  isUpdating: boolean;
  mutationError: string | null;
  isNoticeStale: boolean;
  lastSuccess: MarketingConsentStatus | null;
  update: (status: MarketingConsentStatus) => Promise<void>;
}

interface ScopedState {
  scopeKey: string | null;
  notice: MarketingConsentNotice | null;
  consent: MarketingConsentProjection | null;
  isInitialLoading: boolean;
  initialError: string | null;
  isUpdating: boolean;
  mutationError: string | null;
  isNoticeStale: boolean;
  lastSuccess: MarketingConsentStatus | null;
}

interface MutationRequest {
  controller: AbortController;
  promise: Promise<void>;
}

const emptyState = (
  scopeKey: string | null,
  isInitialLoading = false
): ScopedState => ({
  scopeKey,
  notice: null,
  consent: null,
  isInitialLoading,
  initialError: null,
  isUpdating: false,
  mutationError: null,
  isNoticeStale: false,
  lastSuccess: null,
});

const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

export function useMarketingConsent({
  enabled,
  customerKey,
}: UseMarketingConsentOptions): UseMarketingConsentReturn {
  const scopeKey = useMemo(
    () =>
      enabled && customerKey !== null
        ? `${typeof customerKey}:${String(customerKey)}`
        : null,
    [customerKey, enabled]
  );
  const identityEpoch = apiClient.getAuthIdentityEpoch();
  const owner = useMemo(
    () => ({ scopeKey, identityEpoch }),
    [scopeKey, identityEpoch]
  );
  const ownerRef = useRef<typeof owner | null>(null);
  const isCurrent = useCallback(
    () => ownerRef.current === owner && owner.identityEpoch === apiClient.getAuthIdentityEpoch(),
    [owner]
  );
  const [state, setState] = useState<ScopedState>(() =>
    emptyState(scopeKey, scopeKey !== null)
  );
  const [retryGeneration, setRetryGeneration] = useState(0);
  const stateRef = useRef(state);
  useLayoutEffect(() => { stateRef.current = state; }, [state]);
  const requestGenerationRef = useRef(0);
  const initialControllerRef = useRef<AbortController | null>(null);
  const mutationRequestRef = useRef<MutationRequest | null>(null);

  const abortAll = useCallback(() => {
    initialControllerRef.current?.abort();
    initialControllerRef.current = null;
    mutationRequestRef.current?.controller.abort();
    mutationRequestRef.current = null;
  }, []);

  useLayoutEffect(() => {
    ownerRef.current = owner;
    setState(emptyState(scopeKey, scopeKey !== null));
    return () => {
      if (ownerRef.current === owner) ownerRef.current = null;
      requestGenerationRef.current += 1;
      abortAll();
    };
  }, [abortAll, owner, scopeKey]);

  useEffect(() => {
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    abortAll();
    setState(emptyState(scopeKey, scopeKey !== null));

    if (scopeKey === null) {
      return () => {
        if (requestGenerationRef.current === generation) {
          requestGenerationRef.current += 1;
        }
        abortAll();
      };
    }

    const controller = new AbortController();
    initialControllerRef.current = controller;

    void consentApi
      .getMarketingConsent(controller.signal)
      .then((response) => {
        if (
          !isCurrent() ||
          requestGenerationRef.current !== generation ||
          controller.signal.aborted
        ) {
          return;
        }

        setState({
          scopeKey,
          notice: response.data.notice,
          consent: response.data.consent,
          isInitialLoading: false,
          initialError: null,
          isUpdating: false,
          mutationError: null,
          isNoticeStale: false,
          lastSuccess: null,
        });
      })
      .catch((error: unknown) => {
        if (
          !isCurrent() ||
          requestGenerationRef.current !== generation ||
          controller.signal.aborted ||
          isAbortError(error)
        ) {
          return;
        }

        setState((previous) => ({
          ...previous,
          isInitialLoading: false,
          initialError: errorMessage(
            error,
            'No se pudo cargar el consentimiento de marketing'
          ),
        }));
      })
      .finally(() => {
        if (initialControllerRef.current === controller) {
          initialControllerRef.current = null;
        }
      });

    return () => {
      if (requestGenerationRef.current === generation) {
        requestGenerationRef.current += 1;
      }
      abortAll();
    };
  }, [abortAll, retryGeneration, scopeKey, isCurrent]);

  const retry = useCallback(() => {
    if (isCurrent() && scopeKey !== null) {
      setRetryGeneration((current) => current + 1);
    }
  }, [scopeKey, isCurrent]);

  const update = useCallback(
    (status: MarketingConsentStatus): Promise<void> => {
      if (!isCurrent()) return Promise.resolve();
      const existingRequest = mutationRequestRef.current;
      if (existingRequest !== null) {
        return existingRequest.promise;
      }

      const current = stateRef.current;
      const capability =
        status === 'granted'
          ? current.consent?.can_grant
          : current.consent?.can_withdraw;

      if (
        scopeKey === null ||
        current.scopeKey !== scopeKey ||
        current.notice === null ||
        current.consent === null ||
        capability !== true
      ) {
        return Promise.resolve();
      }

      const generation = requestGenerationRef.current;
      const controller = new AbortController();
      const observedNoticeVersion = current.notice.version;
      setState((previous) => ({
        ...previous,
        isUpdating: true,
        mutationError: null,
        lastSuccess: null,
      }));

      const operation = (async () => {
        try {
          const response = await consentApi.updateMarketingConsent(
            {
              status,
              observed_notice_version: observedNoticeVersion,
            },
            controller.signal
          );
          if (
            !isCurrent() ||
            requestGenerationRef.current !== generation ||
            controller.signal.aborted
          ) {
            return;
          }

          setState((previous) => ({
            ...previous,
            notice: response.data.notice,
            consent: response.data.consent,
            mutationError: null,
            isNoticeStale: false,
            lastSuccess: status,
          }));
        } catch (error: unknown) {
          if (
            !isCurrent() ||
            requestGenerationRef.current !== generation ||
            controller.signal.aborted ||
            isAbortError(error)
          ) {
            return;
          }

          if (
            status === 'granted' &&
            error instanceof MarketingNoticeStaleError
          ) {
            try {
              const refreshed = await consentApi.getMarketingConsent(
                controller.signal
              );
              if (
                !isCurrent() ||
                requestGenerationRef.current !== generation ||
                controller.signal.aborted
              ) {
                return;
              }

              setState((previous) => ({
                ...previous,
                notice: refreshed.data.notice,
                consent: refreshed.data.consent,
                initialError: null,
                mutationError: null,
                isNoticeStale:
                  refreshed.data.notice !== null &&
                  refreshed.data.consent.can_grant,
                lastSuccess: null,
              }));
            } catch (refreshError: unknown) {
              if (
                !isCurrent() ||
                requestGenerationRef.current !== generation ||
                controller.signal.aborted ||
                isAbortError(refreshError)
              ) {
                return;
              }

              setState((previous) => ({
                ...previous,
                notice: null,
                consent: null,
                initialError: errorMessage(
                  refreshError,
                  'No se pudo cargar el consentimiento de marketing'
                ),
                mutationError: null,
                isNoticeStale: false,
                lastSuccess: null,
              }));
            }
            return;
          }

          setState((previous) => ({
            ...previous,
            mutationError: errorMessage(
              error,
              'No se pudo guardar el consentimiento de marketing'
            ),
            lastSuccess: null,
          }));
        } finally {
          if (
            isCurrent() &&
            requestGenerationRef.current === generation &&
            !controller.signal.aborted
          ) {
            setState((previous) => ({
              ...previous,
              isUpdating: false,
            }));
          }
          if (mutationRequestRef.current?.controller === controller) {
            mutationRequestRef.current = null;
          }
        }
      })();

      mutationRequestRef.current = { controller, promise: operation };
      return operation;
    },
    [scopeKey, isCurrent]
  );

  const visibleState =
    state.scopeKey === scopeKey ? state : emptyState(scopeKey, scopeKey !== null);

  return {
    notice: visibleState.notice,
    consent: visibleState.consent,
    isInitialLoading: visibleState.isInitialLoading,
    initialError: visibleState.initialError,
    retry,
    isUpdating: visibleState.isUpdating,
    mutationError: visibleState.mutationError,
    isNoticeStale: visibleState.isNoticeStale,
    lastSuccess: visibleState.lastSuccess,
    update,
  };
}
